import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Container,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material/Select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BASE_URL } from "../constants/global";

export const ProfileForm: React.FC = () => {
  const universityOptions = [
    "University of South Carolina",
    "Baylor University",
    "Florida International University",
    "National Institute of Health",
  ];

  const {
    user,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    isLoading,
  } = useAuth0();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    affiliation: "",
    email: user?.email || "",
    name: user?.name || "",
    subId: user?.sub || "",
    givenName: user?.given_name || "",
  });

  const [formLoading, setFormLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Ensure formData is updated when user loads
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email || "",
        name: user.name || "",
        subId: user.sub || "",
        givenName: user.given_name || "",
      }));
    }
  }, [user]);

  const profileQuery = useQuery({
    queryKey: ['user', 'profile-exists'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/user/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const textResponse = await response.text();
        if (textResponse.trim() === "" || textResponse === "{}") {
          return { exists: false };
        }
        const data = JSON.parse(textResponse);
        if (Object.keys(data).length !== 0) {
          return { exists: true, data };
        }
        return { exists: false };
      }
      throw new Error("Error fetching user profile");
    },
    enabled: isAuthenticated && !!user?.email_verified,
    retry: 3,
    retryDelay: 1000,
    meta: { onError: (err: any) => setFetchError(err.message || 'Error fetching user profile') },
  });

  const profileLoading = profileQuery.isLoading;
  const profileExists = profileQuery.data?.exists ?? null;

  useEffect(() => {
    if (profileExists === true) {
      navigate("/");
    }
  }, [profileExists, navigate]);

  useEffect(() => {
    if (profileQuery.error) {
      setFetchError("Error fetching user profile: " + (profileQuery.error as Error).message);
    }
  }, [profileQuery.error]);

  const registerMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/user/register`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Error creating profile");
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      navigate("/");
    },
  });

  // Handle dropdown change
  const handleChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  // Handle name input change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      name: e.target.value,
    });
  };

  const handleLogin = async () => {
    try {
      await loginWithRedirect({
        appState: {
          returnTo: window.location.pathname,
        },
      });
    } catch (error) {
      console.error("Error during loginWithRedirect:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await registerMutation.mutateAsync(formData);
    } catch (error) {
      console.error("Error creating profile:", error);
    } finally {
      setFormLoading(false);
    }
  };

  // Show loading spinner if profile check is in progress
  if (isLoading || profileLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  // Show fetch error message
  if (fetchError) {
    return (
      <Container maxWidth="sm">
        <h2>Error: {fetchError}</h2>
        <Button variant="outlined" color="inherit" onClick={handleLogin}>
          Retry
        </Button>
      </Container>
    );
  }

  // Show email verification message if the user's email is not verified
  if (!isAuthenticated || !user?.email_verified) {
    return (
      <Container maxWidth="sm">
        <h2>Please check your email to verify your account</h2>
        <Button variant="outlined" color="inherit" onClick={handleLogin}>
          Check verification
        </Button>
        <h3>Not there? Try checking your spam folder.</h3>
      </Container>
    );
  }

  // Show profile form if the profile doesn't exist and the user's email is verified
  if (profileExists === false) {
    return (
      <Container maxWidth="sm">
        <div className="profile-form">
          <form onSubmit={handleSubmit}>
            <h3>Please complete your profile</h3>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleNameChange}
              fullWidth
              margin="normal"
              required
            />
            <FormControl variant="outlined" fullWidth margin="normal">
              <InputLabel id="affiliation-label">Affiliation</InputLabel>
              <Select
                labelId="affiliation-label"
                id="affiliation"
                name="affiliation"
                value={formData.affiliation}
                onChange={handleChange}
                label="Affiliation"
                required
              >
                <MenuItem value="">
                  <em>Select University</em>
                </MenuItem>
                {universityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              fullWidth
              style={{ marginTop: "20px" }}
              disabled={
                formLoading ||
                !formData.name.trim() ||
                !formData.affiliation.trim()
              }
            >
              {formLoading ? <CircularProgress size={24} /> : "Create Profile"}
            </Button>
          </form>
        </div>
      </Container>
    );
  }

  // If no conditions match, return null (should not reach here)
  return null;
};
